
if (!customElements.get('product-info-complete')) {
  customElements.define(
    'product-info-complete',
    class ProductInfoComplete extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent('product-info:loaded', {
            bubbles: true,
          })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');

        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.abortController?.abort();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => {
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) =>
              element.classList.add('scroll-trigger--cancel')
            );
        });

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      /*
       * ------------------------------------------------------------
       * IDENTIFIERS
       * ------------------------------------------------------------
       */

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get blockId() {
        return this.dataset.block;
      }

      get componentId() {
        return `${this.sectionId}-${this.blockId}`;
      }

      /*
       * ------------------------------------------------------------
       * VARIANT CHANGE
       * ------------------------------------------------------------
       */

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;


        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.url !== productUrl;
        const immediateSize = !shouldSwapProduct &&
          this.variantSelectors?.applyImmediateSizeSelection(this, target);
        this.resetProductFormState(immediateSize);


        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState(immediateSize = false) {
        const productForm = this.productForm;

        if (!immediateSize) productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      /*
       * ------------------------------------------------------------
       * PRODUCT SWAP
       * ------------------------------------------------------------
       */

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          /*
           * When loading another product:
           *
           * 1. Full page:
           *    Keep Shopify's original product-info selector.
           *
           * 2. Section/block:
           *    Find the exact product-info-complete using
           *    section.id + block.id.
           */

          let sourceProductInfo;

          if (updateFullPage) {
            sourceProductInfo = html.querySelector(
              "product-info[id^='MainProduct']"
            );
          } else {
            sourceProductInfo = html.getElementById(this.id);

            if (!sourceProductInfo) {
              sourceProductInfo = html.querySelector(
                `product-info-complete[data-section="${this.sectionId}"][data-block="${this.blockId}"]`
              );
            }
          }

          if (!sourceProductInfo) return;

          const variant = this.getSelectedVariant(sourceProductInfo);

          this.variantSelectors?.resolvePendingSelectPromise(
            variant,
            this.getVariantSelects(sourceProductInfo)
          );

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              sourceProductInfo,
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      /*
       * ------------------------------------------------------------
       * FETCH PRODUCT INFO
       * ------------------------------------------------------------
       */

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();

        this.abortController = new AbortController();
        const controller = this.abortController;

        fetch(requestUrl, {
          signal: controller.signal,
        })
          .then((response) => response.text())
          .then((responseText) => {
            if (controller.signal.aborted || !this.isConnected) return;
            this.pendingRequestUrl = null;

            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            callback(html);

            // Hidden recommendation radios must not scroll the entire drawer on refresh.
            document.querySelector(`#${targetId}`)?.focus({
              preventScroll: Boolean(this.closest('cart-complementary-products')),
            });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }

            if (controller === this.abortController) this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(
            parent?.querySelector(selector)?.textContent
          );
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(
          this.getVariantSelects(productInfoNode),
          '[data-selected-variant]'
        );
      }

      /*
       * ------------------------------------------------------------
       * REQUEST URL
       * ------------------------------------------------------------
       *
       * section_id MUST remain section.id because Shopify uses
       * section_id to render the section.
       *
       * block.id is NOT sent as section_id.
       */

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const requestUrl = new URL(url, window.location.origin);

        if (!shouldFetchFullPage) {
          requestUrl.searchParams.set('section_id', this.sectionId);
        }

        if (optionValues.length) {
          requestUrl.searchParams.set('option_values', optionValues.join(','));
        }

        return requestUrl.toString();
      }

      /*
       * ------------------------------------------------------------
       * VARIANT SELECTS
       * ------------------------------------------------------------
       */

      updateOptionValues(html) {
        const sourceProductInfo = this.getSourceProductInfo(html);

        const variantSelects =
          sourceProductInfo?.querySelector('variant-selects');

        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      /*
       * Get the EXACT block from fetched HTML.
       */

      getSourceProductInfo(html) {
        if (!html) return null;

        let source = html.getElementById(this.id);

        if (source) return source;

        source = html.querySelector(
          `product-info-complete[data-section="${this.sectionId}"][data-block="${this.blockId}"]`
        );

        return source;
      }

      /*
       * ------------------------------------------------------------
       * UPDATE PRODUCT INFO
       * ------------------------------------------------------------
       */

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceProductInfo =
            this.getSourceProductInfo(html);

          if (!sourceProductInfo) {
            console.warn(
              'product-info-complete source not found:',
              this.componentId
            );

            return;
          }

          const sourceVariantSelects =
            this.getVariantSelects(sourceProductInfo);

          const variant =
            this.getSelectedVariant(sourceProductInfo);

          /*
           * Resolve promise BEFORE replacing variant-selects.
           */
          this.variantSelectors?.resolvePendingSelectPromise(
            variant,
            sourceVariantSelects
          );

          this.pickupAvailability?.update(variant);

          this.updateOptionValues(html);

          this.updateURL(productUrl, variant?.id);

          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(
            sourceProductInfo,
            variant?.featured_media?.id
          );

          /*
           * --------------------------------------------------------
           * BLOCK-SCOPED UPDATE
           * --------------------------------------------------------
           */

          const updateSourceFromDestination = (
            id,
            shouldHide = () => false
          ) => {
            /*
             * SOURCE:
             * Comes from fetched HTML.
             *
             * DESTINATION:
             * Comes ONLY from current product-info-complete block.
             */

            const source = sourceProductInfo.querySelector(
              `#${id}-${this.componentId}`
            );

            const destination = this.querySelector(
              `#${id}-${this.componentId}`
            );

            if (source && destination) {
              destination.innerHTML = source.innerHTML;

              destination.classList.toggle(
                'hidden',
                shouldHide(source)
              );
            }
          };

          updateSourceFromDestination('price');

          updateSourceFromDestination(
            'Sku',
            ({ classList }) =>
              classList.contains('hidden')
          );

          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) => innerText === ''
          );

          updateSourceFromDestination('Volume');

          updateSourceFromDestination(
            'Price-Per-Item',
            ({ classList }) =>
              classList.contains('hidden')
          );

          this.updateQuantityRules(
            this.componentId,
            sourceProductInfo
          );

          this.querySelector(
            `#Quantity-Rules-${this.componentId}`
          )?.classList.remove('hidden');

          this.querySelector(
            `#Volume-Note-${this.componentId}`
          )?.classList.remove('hidden');

          /*
           * Submit button is also block-specific.
           */

          const sourceSubmitButton =
            sourceProductInfo.querySelector(
              `#ProductSubmitButton-${this.componentId}`
            );

          this.productForm?.toggleSubmitButton(
            sourceSubmitButton?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              blockId: this.blockId,
              componentId: this.componentId,
              html,
              variant,
            },
          });
        };
      }

      /*
       * ------------------------------------------------------------
       * VARIANT INPUTS
       * ------------------------------------------------------------
       */

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `
          #product-form-${this.componentId},
          #product-form-installment-${this.componentId}
          `
        ).forEach((productForm) => {
          const input = productForm.querySelector(
            'input[name="id"]'
          );

          if (!input) return;

          input.value = variantId ?? '';

          input.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        });
      }

      /*
       * ------------------------------------------------------------
       * URL
       * ------------------------------------------------------------
       */

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ''
          }`
        );

        if (this.dataset.updateUrl === 'false') return;

        window.history.replaceState(
          {},
          '',
          `${url}${
            variantId ? `?variant=${variantId}` : ''
          }`
        );
      }

      /*
       * ------------------------------------------------------------
       * UNAVAILABLE
       * ------------------------------------------------------------
       */

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          'price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
        ]
          .map(
            (id) =>
              `#${id}-${this.componentId}`
          )
          .join(', ');

        /*
         * IMPORTANT:
         *
         * Scope to this component.
         * Never use document.querySelectorAll()
         * here because multiple blocks can exist.
         */

        this.querySelectorAll(selectors).forEach(
          ({ classList }) =>
            classList.add('hidden')
        );
      }

      /*
       * ------------------------------------------------------------
       * MEDIA
       * ------------------------------------------------------------
       */

      updateMedia(sourceProductInfo, variantFeaturedMediaId) {
        const cardImage = this.querySelector('.cart-recommendations__image');
        const sourceCardImage = sourceProductInfo?.querySelector('.cart-recommendations__image');
        if (cardImage && sourceCardImage) {
          for (const attribute of ['src', 'srcset', 'sizes', 'alt']) {
            const value = sourceCardImage.getAttribute(attribute);
            if (value === null) cardImage.removeAttribute(attribute);
            else cardImage.setAttribute(attribute, value);
          }
          return;
        }
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource =
          this.querySelector('media-gallery ul');

        const mediaGalleryDestination =
          sourceProductInfo?.querySelector(
            'media-gallery ul'
          );

        const refreshSourceData = () => {
          if (
            this.hasAttribute('data-zoom-on-hover')
          ) {
            enableZoomOnHover(2);
          }

          const mediaGallerySourceItems =
            Array.from(
              mediaGallerySource.querySelectorAll(
                'li[data-media-id]'
              )
            );

          const sourceSet = new Set(
            mediaGallerySourceItems.map(
              (item) => item.dataset.mediaId
            )
          );

          const sourceMap = new Map(
            mediaGallerySourceItems.map(
              (item, index) => [
                item.dataset.mediaId,
                {
                  item,
                  index,
                },
              ]
            )
          );

          return [
            mediaGallerySourceItems,
            sourceSet,
            sourceMap,
          ];
        };

        if (
          mediaGallerySource &&
          mediaGalleryDestination
        ) {
          let [
            mediaGallerySourceItems,
            sourceSet,
            sourceMap,
          ] = refreshSourceData();

          const mediaGalleryDestinationItems =
            Array.from(
              mediaGalleryDestination.querySelectorAll(
                'li[data-media-id]'
              )
            );

          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(
              ({ dataset }) =>
                dataset.mediaId
            )
          );

          let shouldRefresh = false;

          /*
           * Add new media.
           */

          for (
            let i =
              mediaGalleryDestinationItems.length -
              1;
            i >= 0;
            i--
          ) {
            if (
              !sourceSet.has(
                mediaGalleryDestinationItems[i]
                  .dataset.mediaId
              )
            ) {
              mediaGallerySource.prepend(
                mediaGalleryDestinationItems[i]
              );

              shouldRefresh = true;
            }
          }

          /*
           * Remove old media.
           */

          for (
            let i = 0;
            i < mediaGallerySourceItems.length;
            i++
          ) {
            if (
              !destinationSet.has(
                mediaGallerySourceItems[i]
                  .dataset.mediaId
              )
            ) {
              mediaGallerySourceItems[i].remove();

              shouldRefresh = true;
            }
          }

          /*
           * Refresh source data.
           */

          if (shouldRefresh) {
            [
              mediaGallerySourceItems,
              sourceSet,
              sourceMap,
            ] = refreshSourceData();
          }

          /*
           * Sort media to match destination.
           */

          mediaGalleryDestinationItems.forEach(
            (
              destinationItem,
              destinationIndex
            ) => {
              const sourceData =
                sourceMap.get(
                  destinationItem.dataset.mediaId
                );

              if (
                sourceData &&
                sourceData.index !==
                  destinationIndex
              ) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${
                      destinationIndex + 1
                    })`
                  )
                );

                [
                  mediaGallerySourceItems,
                  sourceSet,
                  sourceMap,
                ] = refreshSourceData();
              }
            }
          );
        }

        /*
         * Set featured media ONLY in this block.
         */

        this.querySelector(
          'media-gallery'
        )?.setActiveMedia?.(
          `${this.sectionId}-${variantFeaturedMediaId}`,
          true
        );
        

        /*
         * Update modal ONLY for this block.
         */

        const modalContent =
          this.productModal?.querySelector(
            '.product-media-modal__content'
          );

        const newModalContent =
          sourceProductInfo?.querySelector(
            '.product-media-modal__content'
          );

        if (
          modalContent &&
          newModalContent
        ) {
          modalContent.innerHTML =
            newModalContent.innerHTML;
        }
      }

      /*
       * ------------------------------------------------------------
       * QUANTITY
       * ------------------------------------------------------------
       */

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const data = {
          cartQuantity:
            this.quantityInput.dataset.cartQuantity
              ? parseInt(
                  this.quantityInput.dataset.cartQuantity
                )
              : 0,

          min:
            this.quantityInput.dataset.min
              ? parseInt(
                  this.quantityInput.dataset.min
                )
              : 1,

          max:
            this.quantityInput.dataset.max
              ? parseInt(
                  this.quantityInput.dataset.max
                )
              : null,

          step:
            this.quantityInput.step
              ? parseInt(
                  this.quantityInput.step
                )
              : 1,
        };

        let min = data.min;

        const max =
          data.max === null
            ? data.max
            : data.max - data.cartQuantity;

        if (max !== null) {
          min = Math.min(min, max);
        }

        if (data.cartQuantity >= data.min) {
          min = Math.min(min, data.step);
        }

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        this.quantityInput.value = min;

        publish(
          PUB_SUB_EVENTS.quantityUpdate,
          undefined
        );
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;

        if (!currentVariantId) return;

        this.querySelector(
          '.quantity__rules-cart .loading__spinner'
        )?.classList.remove('hidden');

        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.sectionId}`
        )
          .then((response) => response.text())

          .then((responseText) => {
            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            const sourceProductInfo =
              this.getSourceProductInfo(html);

            if (!sourceProductInfo) return;

            this.updateQuantityRules(
              this.componentId,
              sourceProductInfo
            );
          })

          .catch((e) => console.error(e))

          .finally(() => {
            this.querySelector(
              '.quantity__rules-cart .loading__spinner'
            )?.classList.add('hidden');
          });
      }

      updateQuantityRules(
        componentId,
        sourceProductInfo
      ) {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();

        /*
         * Source is block-specific.
         */

        const quantityFormUpdated =
          sourceProductInfo?.querySelector(
            `#Quantity-Form-${componentId}`
          );

        if (!quantityFormUpdated) return;

        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label',
        ];

        for (const selector of selectors) {
          const current =
            this.quantityForm?.querySelector(
              selector
            );

          const updated =
            quantityFormUpdated.querySelector(
              selector
            );

          if (!current || !updated) continue;

          if (
            selector === '.quantity__input'
          ) {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step',
            ];

            for (const attribute of attributes) {
              const valueUpdated =
                updated.getAttribute(
                  attribute
                );

              if (valueUpdated !== null) {
                current.setAttribute(
                  attribute,
                  valueUpdated
                );
              } else {
                current.removeAttribute(
                  attribute
                );
              }
            }
          } else {
            current.innerHTML =
              updated.innerHTML;

            if (
              selector ===
              '.quantity__label'
            ) {
              const updatedAriaLabelledBy =
                updated.getAttribute(
                  'aria-labelledby'
                );

              if (
                updatedAriaLabelledBy
              ) {
                current.setAttribute(
                  'aria-labelledby',
                  updatedAriaLabelledBy
                );

                const labelId =
                  updatedAriaLabelledBy;

                const currentHiddenLabel =
                  document.getElementById(
                    labelId
                  );

                const updatedHiddenLabel =
                  sourceProductInfo.querySelector(
                    `#${labelId}`
                  );

                if (
                  currentHiddenLabel &&
                  updatedHiddenLabel
                ) {
                  currentHiddenLabel.textContent =
                    updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      /*
       * ------------------------------------------------------------
       * GETTERS
       * ------------------------------------------------------------
       */

      get productForm() {
        return this.querySelector(
          'product-form'
        );
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.componentId}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(
          'pickup-availability'
        );
      }

      get variantSelectors() {
        return this.querySelector(
          'variant-selects'
        );
      }

      get relatedProducts() {
        const relatedProductsSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'related-products'
          );

        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'quick_order_list'
          );

        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }
    }
  );
}

